/**
 * escposLayout.ts — Utilidades compartidas de layout texto plano para térmicas.
 *
 * Compatible con firmware ESC/POS (Code Page PC437): solo ASCII en prefijos
 * y separadores. Usado por ticket de venta, cierre de caja y tablas.
 */

/** Prefijo ASCII para sub-líneas de desglose en térmica (homologado con vista web). */
export const TICKET_SUBLINE_PREFIX = "- ";

/** @deprecated Usar TICKET_SUBLINE_PREFIX */
export const TICKET_THERMAL_SUBLINE_LEGACY = "  |_ ";

/** Ancho monoespaciado por tamaño de papel (cols). */
export const getTicketMaxWidth = (paperWidthMm: 58 | 80): number =>
  paperWidthMm === 80 ? 48 : 32;

/** Tipografía fija del simulador térmico (preview, export PNG/PDF e impresión). */
export const THERMAL_SIM_FONT_PX: Record<58 | 80, number> = {
  80: 9.5,
  58: 7,
};

/** Interlineado del simulador térmico (compacto pero legible). */
export const THERMAL_SIM_LINE_HEIGHT = 1.18;

/** Espaciado vertical del simulador (preview/export; línea en blanco legible). */
export const THERMAL_SIM_LINE_SPACING = {
  blankRatio: 1,
  dividerMyPx: 2,
  totalArticuloMbPx: 2,
  tableHeaderMbPx: 1,
} as const;

/** Ancho mínimo de columna monetaria COP (`$999.999.999` = 11 chars). */
export const THERMAL_MONEY_COL_MIN = 11;

export type ThermalPreviewSection = "header" | "body" | "footer";

const FOOTER_START_PATTERNS = [/^Gestionado con$/i, /^¡?\s*GRACIAS/i];

/** Avanza cabecera → cuerpo → pie (misma regla en preview e impresión). */
export function advanceThermalPreviewSection(
  trimmed: string,
  section: ThermalPreviewSection,
): ThermalPreviewSection {
  if (section === "header") {
    if (/^DETALLE DE VENTA$/i.test(trimmed)) return "body";
    if (/^VENTAS POR/i.test(trimmed)) return "body";
    if (/^Tipo:/i.test(trimmed)) return "body";
    if (/^Fecha:/i.test(trimmed)) return "body";
    if (/^Hora:/i.test(trimmed)) return "body";
    if (/^MOTIVO \/ DETALLE$/i.test(trimmed)) return "body";
    if (
      trimmed.startsWith("+") &&
      trimmed.endsWith("+") &&
      trimmed.includes("-")
    ) {
      return "body";
    }
  }
  if (section === "body") {
    for (const re of FOOTER_START_PATTERNS) {
      if (re.test(trimmed)) return "footer";
    }
  }
  return section;
}

/**
 * Etiqueta de sub-línea: ASCII en térmica (`  |_ `), Unicode ↳ en vista web.
 */
export const formatTicketSubLineLabel = (
  nombre: string,
  thermal: boolean,
): string => (thermal ? `${TICKET_SUBLINE_PREFIX}${nombre}` : `↳ ${nombre}`);

/**
 * Reemplaza emojis y símbolos no imprimibles por equivalentes ASCII y quita
 * los ACENTOS: las térmicas económicas interpretan los bytes UTF-8 de ñ/á/é
 * en su página de códigos (CP437/CP850) y los dibujan corruptos (ej. la ñ
 * sale como "Ã" — una A con tilde arriba). Solo ASCII puro llega a la
 * impresora; la vista web usa sus propios textos.
 */
export const sanitizeForThermal = (text: string): string =>
  (text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    .replace(/[\u2600-\u26FF]/g, "")
    .replace(/🚨\s*/g, "FALTANTE: ")
    .replace(/✅\s*/g, "")
    .replace(/×/g, "x")
    .replace(/↳/g, TICKET_SUBLINE_PREFIX.trimEnd())
    .replace(/\u20ac/g, "EUR")
    // Puntuación tipográfica → ASCII (evita el `?` que las térmicas dibujan
    // por no tener ese carácter en su página de códigos).
    .replace(/[\u2014\u2013]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .normalize("NFC");

/** Envuelve texto largo en líneas de ≤ ancho (corta por palabras; una palabra
 *  que exceda el ancho se parte en trozos balanceados, sin huérfanos de 1
 *  carácter). Misma regla en ticket y etiquetas. */
export const wrapThermalText = (s: string, width: number): string[] => {
  const t = (s || "").replace(/\s+/g, " ").trim();
  if (!t) return [];
  if (width < 2) return t.length ? [t.slice(0, Math.max(1, width))] : [];
  const out: string[] = [];
  let rest = t;
  while (rest.length > width) {
    const cut = rest.lastIndexOf(" ", width);
    if (cut <= 0) {
      // Palabra más larga que la línea: parte en trozos BALANCEADOS para que
      // ninguna quede como huérfano de 1 carácter (ej. `Compromiso` → `Compro`
      // + `miso`, nunca `Compromis` + `o`).
      const wordLen =
        rest.indexOf(" ") === -1 ? rest.length : rest.indexOf(" ");
      const token = rest.slice(0, wordLen);
      const n = Math.ceil(token.length / width);
      const chunk = Math.max(2, Math.ceil(token.length / n));
      out.push(token.slice(0, chunk));
      rest = `${token.slice(chunk)}${rest.slice(wordLen)}`.trim();
      continue;
    }
    out.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) out.push(rest);
  return out;
};

/** Pad a una string a ancho fijo (left/right/center). */
export const padCol = (
  text: string,
  width: number,
  align: "left" | "right" | "center" = "left",
): string => {
  const t = text.length > width ? text.slice(0, width) : text;
  const diff = width - t.length;
  if (diff <= 0) return t;
  if (align === "right") return " ".repeat(diff) + t;
  if (align === "center") {
    const l = Math.floor(diff / 2);
    const r = diff - l;
    return " ".repeat(l) + t + " ".repeat(r);
  }
  return t + " ".repeat(diff);
};

/** Rellena a la derecha una cadena (montos) hasta la columna fija de totales. */
export const padMoneyRight = (raw: string, moneyW: number): string =>
  raw.length >= moneyW ? raw : " ".repeat(moneyW - raw.length) + raw;

/**
 * Fila `label ······ monto` con el monto SIEMPRE completo en su columna:
 * si la etiqueta no cabe se envuelve (nunca se corta el valor, ni el texto).
 * Cada línea queda con ancho exacto = `width`.
 */
export function appendTwoColWrapped(
  lines: string[],
  left: string,
  right: string,
  width: number,
  moneyW = 12,
): void {
  const L = (left || "").replace(/\s+/g, " ").trim();
  const R = (right || "").replace(/\s+/g, " ").trim();
  if (!L && !R) return;
  const labelW = Math.max(8, width - moneyW - 1);
  const lastRow = (label: string) =>
    `${label.padEnd(labelW)} ${padMoneyRight(R, moneyW)}`;
  if (L.length <= labelW) {
    lines.push(sanitizeForThermal(lastRow(L)));
    return;
  }
  const wrapped = wrapThermalText(L, labelW);
  for (let i = 0; i < wrapped.length - 1; i++) {
    lines.push(sanitizeForThermal(wrapped[i].padEnd(width)));
  }
  lines.push(sanitizeForThermal(lastRow(wrapped[wrapped.length - 1] ?? L)));
}

/** Celda de grid con truncado y punto final si desborda. */
export const truncCell = (s: string, w: number): string => {
  const raw = s ?? "";
  // Celdas ya formateadas (centrado / dinero) deben conservar espacios internos.
  if (raw.length === w) return raw;
  const t = raw.replace(/\s+/g, " ").trim();
  if (t.length <= w) return t.padEnd(w, " ");
  if (w <= 1) return t.slice(0, w);
  return t.slice(0, w - 1) + ".";
};

/** Carácter de regla horizontal térmica (continua en Courier; el `-` se ve punteado). */
export const TICKET_THERMAL_HRULE_CHAR = "_";

/** Separador horizontal repetido (underscore, guion, etc.). */
export const emitSepLine = (char: string, maxW: number): string =>
  char.repeat(Math.max(1, maxW));

/** Regla horizontal de sección del ticket (58/80 mm). */
export const emitTicketHRule = (maxW: number): string =>
  emitSepLine(TICKET_THERMAL_HRULE_CHAR, maxW);

/** Regla de sección (`____` / `====`); no incluye rejillas `+---+`. */
export function isThermalSectionDividerLine(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  return /^_+$/.test(t) || /^=+$/.test(t);
}

/**
 * Inserta línea en blanco antes de cada regla de sección si el contenido previo
 * no termina ya en blanco. Estándar global para preview e impresión térmica.
 */
export function withThermalDividerLeadingSpace(lines: string[]): string[] {
  const out: string[] = [];
  for (const line of lines) {
    if (
      isThermalSectionDividerLine(line) &&
      out.length > 0 &&
      out[out.length - 1].trim() !== ""
    ) {
      out.push("");
    }
    out.push(line);
  }
  return out;
}

/** Aire visual en preview antes de reglas cuando el flujo no pasó por texto plano. */
export function withThermalPreviewDividerLeadingSpace(
  lines: ThermalPreviewLine[],
): ThermalPreviewLine[] {
  const out: ThermalPreviewLine[] = [];
  for (const ln of lines) {
    if (
      ln.kind === "divider" &&
      out.length > 0 &&
      out[out.length - 1].kind !== "blank"
    ) {
      out.push({ text: "", align: "left", kind: "blank", bold: false });
    }
    out.push(ln);
  }
  return out;
}

/** Línea separadora de grid: +---+---+ (guiones; distinto de reglas de sección). */
export const gridLineSep = (widths: number[]): string =>
  "+" + widths.map((n) => "-".repeat(Math.max(1, n))).join("+") + "+";

/** Fila de grid con celdas alineadas. */
export const gridRowCells = (cells: string[], widths: number[]): string => {
  const out: string[] = ["|"];
  for (let i = 0; i < widths.length; i++) {
    out.push(truncCell(cells[i] ?? "", widths[i]));
    out.push("|");
  }
  return out.join("");
};

/** ¿La línea debe ir centrada (cabecera, monto, pie)? */
export function shouldCenterThermalPreviewLine(
  line: string,
  section: ThermalPreviewSection,
): boolean {
  const t = line.trim().replace(/^\$\$MONTO\$\$/, "");
  if (!t) return section !== "body";
  if (/^MONTO$/i.test(t)) return true;
  if (/^ID:/i.test(t)) return true;
  if (/^COMPROBANTE DE/i.test(t)) return true;
  if (/^MOTIVO \/ DETALLE$/i.test(t)) return true;
  if (/^<< .+ >>$/.test(t)) return true;
  if (/^REFERENCIA (DEVOLUCION|CREDITO)/i.test(t)) return true;
  if (/^PRODUCTOS (DEVUELTOS|ANULADOS)$/i.test(t)) return true;
  if (/^Gestionado con$/i.test(t)) return true;
  if (/^ALTENOT$/i.test(t)) return true;
  if (/^Comprobante oficial/i.test(t)) return true;
  if (section === "footer") return true;
  if (section === "header") {
    if (/^Tipo:/i.test(t) || /^Fecha:/i.test(t) || /^Hora:/i.test(t)) {
      return false;
    }
    if (/^\$\d/.test(t)) return true;
    if (/^_+$/.test(t)) return false;
    if (shouldLeftAlignThermalPreviewLine(line)) return false;
    return true;
  }
  return false;
}

/** ¿La línea debe ir alineada a la izquierda (tabla / dos columnas / sub-líneas)? */
export function shouldLeftAlignThermalPreviewLine(line: string): boolean {
  const t = line.trimEnd();
  if (!t.trim()) return false;
  if (t.trim().startsWith("$$MONTO$$")) return false;
  if (t.includes("|") || t.includes("│") || t.startsWith("+")) return true;
  if (
    /^[─=]+$/.test(t) ||
    t.includes("┼") ||
    t.includes("┬") ||
    t.includes("┴")
  ) {
    return true;
  }
  if (
    line.startsWith(TICKET_SUBLINE_PREFIX) ||
    line.startsWith(TICKET_THERMAL_SUBLINE_LEGACY) ||
    /^\s{2}\|_/.test(line)
  )
    return true;
  if (/^-\s/.test(t)) return true;
  if (/^\s{2}/.test(line) && /\$\d/.test(line)) return true;
  if (/^\*/.test(t.trim())) return true;
  if (/Producto\s/i.test(t) && /Total/i.test(t)) return true;
  if (/^-\$\d/.test(t.trim())) return true;
  if (/^\+?\s*\$/.test(t.trim()) && /\s{3,}/.test(t)) return true;
  if (/Total Art/i.test(t) && /\$\d/.test(t)) return true;
  if (/:\s/.test(t) && /\$\d/.test(t) && /\s{3,}/.test(t)) return true;
  const dollars = (t.match(/\$/g) || []).length;
  if (dollars >= 2) return true;
  if (dollars === 1 && t.length > 16 && /\s{4,}/.test(t)) return true;
  return false;
}

export type ThermalPreviewLineAlign = "left" | "center";

export type ThermalPreviewLineKind =
  | "default"
  | "blank"
  | "divider"
  | "totalArticulo"
  | "tableHeader";

export interface ThermalPreviewLine {
  text: string;
  align: ThermalPreviewLineAlign;
  kind: ThermalPreviewLineKind;
  /** Negrita ESC/POS (títulos, totales). */
  bold?: boolean;
}

export function detectThermalPreviewLineKind(
  line: string,
  section: ThermalPreviewSection,
): ThermalPreviewLineKind {
  const trimmed = line.trim();
  if (!trimmed) return "blank";
  if (/^_+$/.test(trimmed)) return "divider";
  if (/Total Art/i.test(trimmed)) return "totalArticulo";
  if (
    section === "body" &&
    (/Cant/i.test(trimmed) || /CANT/i.test(trimmed)) &&
    /Producto|Prod/i.test(trimmed) &&
    /Total|Tot/i.test(trimmed)
  ) {
    return "tableHeader";
  }
  if (
    section === "body" &&
    /Producto/i.test(trimmed) &&
    /P\.?Unit/i.test(trimmed) &&
    /Total/i.test(trimmed)
  ) {
    return "tableHeader";
  }
  return "default";
}

/**
 * Centra solo cabecera (negocio/fecha) y pie (agradecimiento/links).
 * Todo el cuerpo del ticket (detalle, pagos, notas) queda alineado a la izquierda.
 */
export function prepareThermalPreviewLine(
  line: string,
  cols: number,
  section: ThermalPreviewSection = "header",
): ThermalPreviewLine {
  const kind = detectThermalPreviewLineKind(line, section);
  if (line.length === 0) {
    return {
      text: "",
      align: section === "body" ? "left" : "center",
      kind: "blank",
      bold: false,
    };
  }
  const trimmed = line.trim();
  if (/^_+$/.test(trimmed) || /^=+$/.test(trimmed) || trimmed.includes("┼")) {
    return {
      text: line.length >= cols ? line.slice(0, cols) : line.padEnd(cols, " "),
      align: "left",
      kind: "divider",
      bold: false,
    };
  }
  if (/^\+[-+]+$/.test(trimmed)) {
    return {
      text: line.length >= cols ? line.slice(0, cols) : line.padEnd(cols, " "),
      align: "left",
      kind: "divider",
      bold: false,
    };
  }
  if (shouldCenterThermalPreviewLine(line, section)) {
    const display = trimmed.replace(/^\$\$MONTO\$\$/, "");
    return {
      text: display.slice(0, cols),
      align: "center",
      kind,
      bold: false,
    };
  }
  if (section === "body" || shouldLeftAlignThermalPreviewLine(line)) {
    const text =
      line.length >= cols ? line.slice(0, cols) : line.padEnd(cols, " ");
    return { text, align: "left", kind, bold: false };
  }
  return { text: trimmed.slice(0, cols), align: "center", kind, bold: false };
}
